import { NATIVE_TOKEN_ADDRESS } from '@sirius-cybernetics-42/product-config'

import { getActiveChainId } from '@/config/active'
import { addressesEquivalent } from '@/data/marketApi/normalizeMarketToken'
import type { PoolIdentity } from '@/data/marketApi/types'
import { Currency, Token } from '@/sdk'

function currencyAddress(currency: Currency): string {
  return currency instanceof Token ? currency.address.toLowerCase() : NATIVE_TOKEN_ADDRESS
}

export type MarketPairLegs = {
  /** Catalog / Nutrimatic base leg as a wallet Currency (matched from the trade pair). */
  base: Currency | undefined
  /** Catalog / Nutrimatic quote leg. */
  quote: Currency | undefined
  /** Stable market label (e.g. FOXXEY/42) — never follows trade direction. */
  pairLabel: string
}

function sameSymbol(currency: Currency, symbol: string | undefined): boolean {
  if (!symbol) return false
  return (currency.symbol ?? '').toUpperCase() === symbol.toUpperCase()
}

/**
 * Market presentation legs from Nutrimatic/indexed pool identity.
 * Trade input/output only locate the pool; they do not define display order.
 */
export function resolveMarketPairLegs(
  currencyA?: Currency | null,
  currencyB?: Currency | null,
  pool?: PoolIdentity | null,
): MarketPairLegs {
  const baseRef = pool?.baseToken ?? pool?.token0
  const quoteRef = pool?.quoteToken ?? pool?.token1
  const pairLabel =
    pool?.pair ??
    (baseRef?.symbol && quoteRef?.symbol
      ? `${baseRef.symbol}/${quoteRef.symbol}`
      : '—')

  if (!currencyA || !currencyB) {
    return {
      base: currencyA ?? undefined,
      quote: currencyB ?? undefined,
      pairLabel,
    }
  }

  if (!baseRef) {
    // No catalog identity yet — still avoid inventing an order from trade direction.
    return { base: undefined, quote: undefined, pairLabel }
  }

  const chainId = getActiveChainId()
  const aAddr = currencyAddress(currencyA)
  const bAddr = currencyAddress(currencyB)
  const baseAddr = baseRef.address

  if (addressesEquivalent(aAddr, baseAddr, chainId)) {
    return { base: currencyA, quote: currencyB, pairLabel }
  }
  if (addressesEquivalent(bAddr, baseAddr, chainId)) {
    return { base: currencyB, quote: currencyA, pairLabel }
  }

  // Native / wrapped edge cases: fall back to catalog symbol match.
  if (sameSymbol(currencyA, baseRef.symbol)) {
    return { base: currencyA, quote: currencyB, pairLabel }
  }
  if (sameSymbol(currencyB, baseRef.symbol)) {
    return { base: currencyB, quote: currencyA, pairLabel }
  }

  return { base: undefined, quote: undefined, pairLabel }
}
